// @givmo/passkeys — the escape hatch's routes on the web (AS-WEB26): the signed-out way back in for
// someone whose company locks how they sign in (AS-BE16, served while any lock lives: AS-BE51).
//
//   - `POST /authn/escape-hatch/request` `{email}` → 202 with an empty body WHATEVER the address
//     (enumeration-safe). A request inside 60 s of the last code mints nothing (the resend interval),
//     and neither does one past the account's hourly allowance; both still answer 202. Per IP, 20 an
//     hour, then 429.
//   - `POST /authn/escape-hatch/verify` `{email, code}` → 204 for every proof of inbox control, even
//     for an account with no company left (the repair path, and the uniform answer). A 204 has
//     unlinked the company (employee-gated content dropped at once), cleared the lock, ended the
//     account's sessions and mailed the person. One uniform 400 (`ESCAPE_HATCH_VERIFICATION_FAILED`)
//     for an unknown address or a wrong, expired, spent or other-purpose code; 429 per IP, or once the
//     account has failed ten times in an hour; a 5xx leaves the code unspent.
//   - `GET /authn/escape-hatch` → `{sign_in_lock_exists}`: whether any sign-in lock exists here, so a
//     sign-in page shows its link only where somebody could be locked out (D-AS-48 3a). Sized for page
//     loads; never names anyone.
//   - All three: the absent-route 404 while the hatch is not served (no lock state anywhere and
//     `EMPLOYER_SSO_ENABLED` off), for any body.
//
// ⚠ NOT IN THE COMMITTED SNAPSHOT: the backend mounts `/authn` as its own sub-app (the account reset's
// reason, `factor-reset-transport.ts`), so these ride the sanctioned `givmoClient` calls and inherit
// the interceptor stack. Unauthenticated by design: the person holds no session.

import { givmoClient, isGivmoError } from '@givmo/api-client';

const REQUEST_URL = '/authn/escape-hatch/request';
const VERIFY_URL = '/authn/escape-hatch/verify';
const LOCK_STATE_URL = '/authn/escape-hatch';

/** Ask for a code to the account's address (always 202: nothing here says whether one went out). */
export async function requestEscapeHatchCode(
  email: string,
  init?: { signal?: AbortSignal },
): Promise<void> {
  const { error } = await givmoClient.post({
    url: REQUEST_URL,
    body: { email },
    signal: init?.signal,
  });
  if (error !== undefined && error !== null) throw error;
}

/** Spend the code: unlink the company and restore personal sign-in (204). */
export async function verifyEscapeHatchCode(
  email: string,
  code: string,
  init?: { signal?: AbortSignal },
): Promise<void> {
  const { error } = await givmoClient.post({
    url: VERIFY_URL,
    body: { email, code: code.trim() },
    signal: init?.signal,
  });
  if (error !== undefined && error !== null) throw error;
}

/** What a failed call means to the person: one of the flow's states. */
export type EscapeHatchFailure = 'codeRejected' | 'throttled' | 'dark' | 'unavailable';

export function escapeHatchFailureOf(thrown: unknown): EscapeHatchFailure {
  if (isGivmoError(thrown)) {
    if (thrown.status === 400) return 'codeRejected';
    if (thrown.status === 429) return 'throttled';
    if (thrown.status === 404) return 'dark';
  }
  // A transport failure, a 5xx, anything unreadable: nothing was learned, so try again later.
  return 'unavailable';
}

/**
 * What the lock read said, for the entry's cache (`escape-hatch-entry.tsx`):
 *   - `shown`   — a sign-in lock exists here (or the backend's read failed, which answers True);
 *   - `hidden`  — none does, or the hatch is dark (the 404);
 *   - `unknown` — a 429, a 5xx or no answer: says nothing about whether a lock exists. The entry
 *                 SHOWS the link on it and asks again a minute later (`escape-hatch-entry.tsx`).
 * Never throws, and silent on every path: a dark 404 is the EXPECTED answer on most sign-in pages.
 */
export type EscapeHatchEntryVerdict = 'shown' | 'hidden' | 'unknown';

export async function readEscapeHatchEntry(init?: {
  signal?: AbortSignal;
}): Promise<EscapeHatchEntryVerdict> {
  try {
    const { data, error } = await givmoClient.get({ url: LOCK_STATE_URL, signal: init?.signal });
    if (error !== undefined && error !== null) {
      return isGivmoError(error) && error.status === 404 ? 'hidden' : 'unknown';
    }
    const exists = (data as { sign_in_lock_exists?: unknown } | undefined)?.sign_in_lock_exists;
    if (exists === true) return 'shown';
    if (exists === false) return 'hidden';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}
